/**
 * 협력사 상세 정보 타입 정의
 */

import { formatBirthDate, formatPhoneNumber, formatBusinessNumber } from "@/shared/utils/formatter";
import { getSupplierStatusLabel } from "@/shared/utils/labelConverterHelpers";

/** 협력사 상세 정보 DTO */
export interface SupplierInfoDto {
    suppId: string;                 // 협력사 ID
    suppName: string;               // 협력사명
    statusCode: string;             // 협력사 상태 코드 (OPERATING, TERMINATED 등)
    suppNum: string;                // 사업자번호
    suppCompName: string;           // 사업자상호
    compCeoName: string;            // 대표자명
    managerName: string;            // 관리자명
    mngPhoneNum: string;            // 관리자 휴대폰번호
    deliveryArea: string | null;    // 배달권역
    accountNum: string | null;      // 계좌번호
    bankCode: string | null;        // 은행코드
    bankName: string | null;        // 은행명
    policyCode: string | null;      // 정책코드
    preAdjustYn: string | null;     // 선정산 여부
    withdrawalYn: string | null;    // 출금여부
    gradeName?: string | null;      // 등급명
    endMngDay: string | null;       // 운영종료일 (YYYYMMDD)
    insDate: string | null;         // 등록일자
    insUser: string | null;         // 등록자
    updDate: string | null;         // 수정일자
    updUser: string | null;         // 수정자
    addrInfo?: string | null;       // 주소 정보
    suppLvl?: string | null;        // 협력사 등급코드
    slotSetCnt?: number | null;     // 슬롯 세트 수
    suppType?: string | null;       // 협력사 타입 (직영/가맹)
    dpBankCd?: string | null;       // 입금 은행코드
    dpAcctNo?: string | null;       // 입금 계좌번호
    dpAccName?: string | null;      // 입금 예금주명
    acctName?: string | null;       // 계좌 예금주명 (ACCT_NAME)
    bizType?: string | null;        // 업태
    bizItem?: string | null;        // 종목
    bizClass?: string | null;       // 사업자종류
    taxType?: string | null;        // 과세자 유형
    email?: string | null;          // 이메일 주소
}

/** 협력사 정보 조회 응답 DTO */
export interface GetSupplierInfoResponseDto {
    supplier: SupplierInfoDto;
}

/** 협력사 상세 정보 Domain Model */
export interface SupplierInfo {
    supplierId: string;
    supplierName: string;
    supplierGrade: string;  // 등급 표시명 (e.g., "골드")
    suppLvl: string;        // 등급 코드 (e.g., "G01") - 수정 시 사용
    slotSetCnt: string;     // 슬롯 세트 수
    mobilePhone: string;
    status: string;
    registrationDate: string;
    operationEndDate: string;
    phone: string;
    address: string;
    deliveryArea: string;
    suppType: string;       // 협력사 타입 (직영/가맹)

    // 제휴사 정보
    bankCode: string;
    bankName: string;
    accountNumber: string;
    accountHolder: string;

    // 사업자 정보
    companyName: string;
    businessNumber: string;
    representative: string;
    bizType: string;        // 업태
    bizItem: string;        // 종목
    bizClass: string;       // 사업자종류
    taxType: string;        // 과세자 유형
    email: string;          // 이메일 주소

    // 입금 정보
    dpBankCode: string;
    dpBankName: string;
    dpAccountNumber: string;
    dpAccountName: string;
}


/** 은행 코드를 은행명으로 변환 */
export const getBankName = (bankCode: string | null): string => {
    if (!bankCode) return '';

    const bankMap: { [key: string]: string } = {
        '011': '농협은행',
        '004': '국민은행',
        '088': '신한은행',
        '020': '우리은행',
        '081': '하나은행',
        '003': '기업은행',
        '023': 'SC제일은행',
        '027': '한국씨티은행',
        '신한은행': '신한은행'  // 이미 한글인 경우 그대로 반환
    };
    return bankMap[bankCode] || bankCode;
};

/** 협력사 정산 정보 수정 요청 DTO */
export interface UpdateSupplierSettlementRequest {
    bankCode: string;       // 은행코드
    accountNum: string;     // 계좌번호
    acctName: string;       // 예금주 (ACCT_NAME)
}

/** 협력사 사업자 정보 수정 요청 DTO */
export interface UpdateSupplierBusinessInfoRequest {
    suppCompName: string;       // 사업자상호(법인명)
    suppNum: string;            // 사업자등록번호
    compCeoName: string;        // 대표자명
    bizType: string;            // 업태
    bizItem: string;            // 종목
    bizClass: string;           // 사업자종류
    taxType: string;            // 과세자 유형
}

/** 협력사 기본 정보 수정 요청 DTO */
export interface UpdateSupplierBasicInfoRequest {
    mngPhoneNum: string;        // 관리자 휴대폰번호
    addrInfo: string;           // 사업장 주소
    suppType: string;           // 협력사 타입 (직영/가맹)
    suppLvl?: string;           // 협력사 등급 코드
    slotSetCnt?: number;        // 슬롯 세트 수
}

/** 일차감(공제) 정보 DTO - 백엔드 응답과 매핑 */
export interface DeductDto {
    seqNo: number;              // 순번
    deductTarget: string;       // 공제 대상 (S: 협력사, R: 라이더)
    suppId: string | null;      // 협력사 ID
    riderId: string | null;     // 라이더 ID
    deductType: string;         // 공제 타입
    calcType: string;           // 계산 타입
    deductName: string;         // 공제 이름
    deductCnt: number | null;   // 공제 횟수
    deductAmt: number | null;   // 공제 금액
    deductRate: number | null;  // 공제 비율
    deductDispYn: string;       // 공제 표시 여부
    startDay: string;           // 시작일
    endDay: string | null;      // 종료일
    insDate: string;            // 등록일
    insUser: string;            // 등록자
    updUser: string | null;     // 수정자
    updDate: string | null;     // 수정일
}

/** 일차감 목록 조회 응답 DTO */
export interface GetDeductListResponseDto {
    totalCount: number;
    deducts: DeductDto[];
}

/** DTO -> Domain 변환 */
export const supplierInfoFromDto = (dto: SupplierInfoDto): SupplierInfo => ({
    supplierId: dto.suppId,
    supplierName: dto.suppName,
    supplierGrade: dto.gradeName || '-',
    suppLvl: dto.suppLvl || '',
    slotSetCnt: dto.slotSetCnt != null ? String(dto.slotSetCnt) : '',
    mobilePhone: formatPhoneNumber(dto.mngPhoneNum),
    status: getSupplierStatusLabel(dto.statusCode),
    registrationDate: dto.insDate ? formatBirthDate(dto.insDate) : '-',
    operationEndDate: dto.endMngDay ? formatBirthDate(dto.endMngDay) : '-',
    phone: '',             // DB에 없는 필드
    address: dto.addrInfo || '',           // 주소 정보
    deliveryArea: dto.deliveryArea || '',
    suppType: dto.suppType === 'F' ? '가맹' : dto.suppType === 'D' ? '직영' : (dto.suppType || '-'),      // 협력사 타입 변환

    // 제휴사 정보
    bankCode: dto.bankCode || '',
    bankName: dto.bankName || getBankName(dto.bankCode),  // API 응답에 bankName이 있으면 사용, 없으면 bankCode로 변환
    accountNumber: dto.accountNum || '',
    accountHolder: dto.acctName || '',

    // 사업자 정보
    companyName: dto.suppCompName,
    businessNumber: formatBusinessNumber(dto.suppNum),
    representative: dto.compCeoName,
    bizType: dto.bizType || '',
    bizItem: dto.bizItem || '',
    bizClass: dto.bizClass || '',
    taxType: dto.taxType || '',
    email: dto.email || '',

    // 입금 정보
    dpBankCode: dto.dpBankCd || '',
    dpBankName: dto.dpBankCd ? getBankName(dto.dpBankCd) : '',
    dpAccountNumber: dto.dpAcctNo || '',
    dpAccountName: dto.dpAccName || ''
});
